import { useRef, useState } from "react";
import { Search, Trophy } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AIInsightPanel } from "@/components/revenueos/AIInsightPanel";

/**
 * PageToolbar — search + in-place AI insight (B-13).
 * Spar prompts render insight directly below the toolbar, not in a global modal.
 */
export function PageToolbar({
  query,
  onQueryChange,
  placeholder = "Search…",
  entityLabel = "items",
  sparPrompts,
  right = null,
  afterPrompts = null,
  count = null,
  variant = "default",
}) {
  const sessionRef = useRef(`spar-${entityLabel}-${Date.now()}`);
  const [spar, setSpar] = useState(null); // { label, insight, loading, error }

  const defaultPrompts = [
    { icon: Trophy, label: "How to win?", seed: `Based on my current ${entityLabel}, how do I win more of them this quarter? Give me a prioritized playbook to close, spar with me on strategy, and flag the 3 highest-leverage moves I can make in the next 7 days.` },
  ];
  const prompts = sparPrompts || defaultPrompts;
  const isUniverse = variant === "universe";

  async function runSpar(p) {
    setSpar({ label: p.label, insight: null, loading: true, error: null });
    try {
      const { data } = await api.post("/copilot/message", {
        session_id: sessionRef.current,
        message: p.seed,
        context_type: "dashboard",
      });
      setSpar({ label: p.label, insight: data.reply, loading: false, error: null });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not generate insight";
      setSpar({ label: p.label, insight: null, loading: false, error: msg });
      toast.error("AI insight failed");
    }
  }

  return (
    <div className="space-y-3" data-testid="page-toolbar-wrap">
      <div className={isUniverse
        ? "flex flex-col gap-4"
        : "border border-black/10 rounded-md bg-white p-2.5 md:p-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-3"
      } data-testid="page-toolbar">
        <div className={isUniverse
          ? "w-full shrink-0 flex items-center gap-3 px-6 h-14 rounded-full bg-white border border-[#BFD5E0] shadow-[0_12px_30px_rgba(7,83,154,0.17)] focus-within:border-[#00A2D4] transition-colors"
          : "flex-1 flex items-center gap-2 px-2.5 h-9 rounded-md bg-[var(--saarv-blue-soft)] border border-transparent focus-within:border-[var(--saarv-blue)]/40 transition-colors"
        }>
          <Search size={isUniverse ? 18 : 13} className="text-[var(--saarv-blue)] shrink-0" />
          <input
            value={query || ""}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={placeholder}
            data-testid="page-toolbar-search"
            className={`flex-1 bg-transparent outline-none placeholder:text-[var(--text-dim)] text-[var(--text)] ${isUniverse ? "text-[15px]" : "text-[13px]"}`}
          />
          {count !== null && (
            <span className="text-[10px] font-mono text-[var(--text-dim)] shrink-0" data-testid="page-toolbar-count">{count}</span>
          )}
        </div>

        <div className={isUniverse ? "flex items-center justify-between gap-3 flex-wrap" : "contents"}>
          <div className={isUniverse ? "flex items-center gap-2 overflow-x-auto" : "flex items-center gap-1.5 md:border-l md:border-black/10 md:pl-3 overflow-x-auto"}>
            {prompts.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => runSpar(p)}
              disabled={spar?.loading}
              data-testid={`page-toolbar-spar-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              title={p.seed}
              className={`shrink-0 h-9 px-3 ${isUniverse ? "rounded-full" : "rounded-lg"} border text-[12px] font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50 ${
                isUniverse
                  ? spar?.label === p.label
                    ? "border-[#07539A] bg-[#07539A] text-[#FFFFFF]"
                    : "border-[#1687EA] bg-[#1687EA] text-[#FFFFFF] hover:bg-[#0875D1]"
                  : spar?.label === p.label
                    ? "border-[var(--copper)] bg-[var(--copper)]/10 text-[var(--copper-strong)]"
                    : "border-[var(--copper)]/25 bg-[var(--copper)]/[0.04] hover:bg-[var(--copper)]/[0.08] hover:border-[var(--copper)]/50 text-[var(--copper-strong)]"
              }`}
            >
              <p.icon size={12} strokeWidth={1.8} />
              <span className="whitespace-nowrap">{isUniverse ? `Ask Niq · ${p.label}` : p.label}</span>
            </button>
            ))}
            {afterPrompts}
          </div>
          {right}
        </div>
      </div>

      {spar && (
        <AIInsightPanel
          title={spar.label}
          insight={spar.insight}
          loading={spar.loading}
          error={spar.error}
          onDismiss={() => setSpar(null)}
          onRegenerate={() => {
            const p = prompts.find((x) => x.label === spar.label);
            if (p) runSpar(p);
          }}
          emptyText="Generating insight…"
          anchorId="page-toolbar-ai-insight"
          markdown
          accent={isUniverse ? "navy" : "copper"}
        />
      )}
    </div>
  );
}
